import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ChefHat, Sparkles, CheckCircle, AlertCircle, Trash2, Edit2 } from 'lucide-react';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AddRecipeBatch({ user, onLogout }) {
  const [recipeText, setRecipeText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsedRecipes, setParsedRecipes] = useState([]);
  const [mealType, setMealType] = useState('Breakfast');
  const [categories, setCategories] = useState([]);
  const [allergens, setAllergens] = useState([]);
  const [proteinSources, setProteinSources] = useState([]);
  const [saving, setSaving] = useState(false);
  const [foodBankItems, setFoodBankItems] = useState([]);

  const availableCategories = [
    { value: 'standard', label: 'Standard', description: 'No restrictions' },
    { value: 'standard', label: 'Standard', description: 'No dietary restrictions' },
    { value: 'vegetarian', label: 'Vegetarian', description: 'No meat, includes eggs/dairy' },
    { value: 'vegan', label: 'Vegan', description: 'Plant-based only' },
    { value: 'pescatarian', label: 'Pescatarian', description: 'Fish/seafood, no meat' },
    { value: 'keto', label: 'Keto', description: 'Low-carb, high-fat' },
    { value: 'halal', label: 'Halal', description: 'Islamic dietary laws' },
    { value: 'kosher', label: 'Kosher', description: 'Jewish dietary laws' },
    { value: 'gluten_free', label: 'Gluten-Free', description: 'No wheat/gluten' },
    { value: 'dairy_free', label: 'Dairy-Free', description: 'No dairy products' },
    { value: 'high_protein', label: 'High Protein', description: '40g+ protein per serving' },
    { value: 'quick_meals', label: 'Quick Meals', description: '<20 min prep' }
  ];

  const toggleCategory = (categoryValue) => {
    setCategories(prev => 
      prev.includes(categoryValue)
        ? prev.filter(c => c !== categoryValue)
        : [...prev, categoryValue]
    );
  };

  useEffect(() => {
    fetchFoodBank();
  }, []);

  const fetchFoodBank = async () => {
    try {
      const response = await axios.get(`${API}/food-bank?limit=500`);
      const items = Array.isArray(response.data) 
        ? response.data 
        : (response.data.foods || []);
      setFoodBankItems(items);
    } catch (error) {
      console.error('Failed to fetch Food Bank:', error);
    }
  };

  // Helper function to extract metadata from recipe text
  const extractMetadata = (recipeText) => {
    const metadata = {
      mealType: null,
      recipeName: null,
      categories: [],
      allergens: [],
      proteinSources: [],
      estimatedTime: null
    };

    const lines = recipeText.split('\n');
    
    for (const line of lines) {
      const lowerLine = line.toLowerCase().trim();
      
      // Extract Recipe Name
      if (lowerLine.startsWith('recipe name:')) {
        const value = line.split(':')[1]?.trim();
        metadata.recipeName = value;
      }
      
      // Extract Meal Type and normalize case
      if (lowerLine.startsWith('meal type:')) {
        const value = line.split(':')[1]?.trim();
        // Normalize: "breakfast" -> "Breakfast", "DINNER" -> "Dinner"
        if (value) {
          metadata.mealType = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
        }
      }
      
      // Extract Categories
      if (lowerLine.startsWith('categories:')) {
        const value = line.split(':')[1]?.trim();
        if (value && value.toLowerCase() !== 'none' && value.toLowerCase() !== 'blank') {
          const cats = value.split(',').map(c => c.trim().toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_'));
          metadata.categories = cats;
        }
      }
      
      // Extract Allergens
      if (lowerLine.startsWith('allergens:')) {
        const value = line.split(':')[1]?.trim();
        if (value && value.toLowerCase() !== 'none' && value.toLowerCase() !== 'blank') {
          const alls = value.split(',').map(a => a.trim().toLowerCase());
          metadata.allergens = alls;
        }
      }
      
      // Extract Protein Sources
      if (lowerLine.startsWith('protein sources:') || lowerLine.startsWith('protein source:')) {
        const value = line.split(':')[1]?.trim();
        if (value && value.toLowerCase() !== 'none' && value.toLowerCase() !== 'blank') {
          const prots = value.split(',').map(p => p.trim().toLowerCase().replace('-', '_'));
          metadata.proteinSources = prots;
        }
      }
      
      // Extract Estimated Time
      if (lowerLine.startsWith('estimated time:') || lowerLine.startsWith('cooking time:')) {
        const value = line.split(':')[1]?.trim();
        metadata.estimatedTime = value;
      }
    }
    
    return metadata;
  };

  // Helper function to strip metadata lines from recipe text
  const stripMetadata = (recipeText) => {
    const lines = recipeText.split('\n');
    const cleanedLines = lines.filter(line => {
      const lowerLine = line.toLowerCase().trim();
      return !(
        lowerLine.startsWith('recipe name:') ||
        lowerLine.startsWith('meal type:') ||
        lowerLine.startsWith('categories:') ||
        lowerLine.startsWith('allergens:') ||
        lowerLine.startsWith('protein sources:') ||
        lowerLine.startsWith('protein source:') ||
        lowerLine.startsWith('estimated time:') ||
        lowerLine.startsWith('cooking time:')
      );
    });
    return cleanedLines.join('\n');
  };

  const handleBatchParse = async () => {
    if (!recipeText.trim()) {
      toast.error('Please enter recipe text');
      return;
    }

    setParsing(true);
    try {
      // Split recipes by "---" separator
      const recipeTexts = recipeText
        .split(/---+/)
        .map(text => text.trim())
        .filter(text => text.length > 0);

      if (recipeTexts.length === 0) {
        toast.error('No recipes found. Make sure recipes are separated by ---');
        setParsing(false);
        return;
      }

      if (recipeTexts.length > 20) {
        toast.error('Maximum 20 recipes per batch. Please split into smaller batches.');
        setParsing(false);
        return;
      }

      toast.info(`Parsing ${recipeTexts.length} recipes...`);

      // Parse each recipe
      const parsedResults = [];
      for (let i = 0; i < recipeTexts.length; i++) {
        try {
          // Extract metadata first
          const metadata = extractMetadata(recipeTexts[i]);
          
          // Strip metadata lines before sending to parser
          const cleanedRecipeText = stripMetadata(recipeTexts[i]);
          
          const response = await axios.post(`${API}/recipes/parse`, {
            recipe_text: cleanedRecipeText,
            meal_type: metadata.mealType || mealType
          });

          const data = response.data;
          
          // Merge auto-detected allergens and extracted metadata with parsed metadata
          const enrichedMetadata = {
            ...metadata,
            allergens: data.auto_detected_allergens || metadata.allergens || [],
            dietary_categories: data.metadata?.dietary_categories || metadata.dietary_categories || metadata.categories,
            // Add new extracted metadata from backend (keep snake_case from backend)
            budget_category: data.metadata?.budget_category || metadata.budget_category || metadata.budgetCategory,
            cooking_time: data.metadata?.cooking_time || metadata.cooking_time || metadata.cookingTime,
            spice_level: data.metadata?.spice_level || metadata.spice_level || metadata.spiceLevel,
            main_protein: data.metadata?.main_protein || metadata.main_protein || metadata.mainProtein,
            meal_prep_style: data.metadata?.meal_prep_style || metadata.meal_prep_style || metadata.mealPrepStyle || 'mix'
          };
          
          parsedResults.push({
            id: `temp_${Date.now()}_${i}`,
            title: metadata.recipeName || data.title || `Recipe ${i + 1}`,
            instructions: data.instructions || '',
            ingredients: (data.parsed_ingredients || []).map(ing => ({
              raw_text: ing.raw_text || '',
              ingredient_name: ing.ingredient_name || '',
              grams: ing.grams || 0,
              food_bank_id: ing.food_bank_id || null,
              food_bank_match: ing.food_bank_match || '',
              confidence: ing.confidence || 0,
              display_text: ing.raw_text || ''
            })),
            confidence: data.total_confidence || 0,
            status: 'success',
            // Store metadata with each recipe (including auto-detected allergens)
            metadata: enrichedMetadata
          });
        } catch (error) {
          parsedResults.push({
            id: `temp_${Date.now()}_${i}`,
            title: `Recipe ${i + 1}`,
            status: 'error',
            error: error.response?.data?.detail || 'Failed to parse',
            metadata: extractMetadata(recipeTexts[i])
          });
        }
      }

      setParsedRecipes(parsedResults);
      
      const successCount = parsedResults.filter(r => r.status === 'success').length;
      const errorCount = parsedResults.filter(r => r.status === 'error').length;
      
      if (successCount > 0) {
        toast.success(`Parsed ${successCount} recipes successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}`);
      } else {
        toast.error('All recipes failed to parse');
      }

    } catch (error) {
      toast.error('Batch parsing failed');
      console.error(error);
    } finally {
      setParsing(false);
    }
  };

  const handleSaveAll = async () => {
    const successfulRecipes = parsedRecipes.filter(r => r.status === 'success');
    
    if (successfulRecipes.length === 0) {
      toast.error('No recipes to save');
      return;
    }

    setSaving(true);
    try {
      let savedCount = 0;
      let failedCount = 0;

      for (const recipe of successfulRecipes) {
        try {
          // Use metadata from parsed recipe if available, otherwise use global selections
          const recipeMealType = recipe.metadata?.mealType || mealType;
          const recipeCategories = recipe.metadata?.dietary_categories?.length > 0 
            ? recipe.metadata.dietary_categories 
            : (recipe.metadata?.categories?.length > 0 
              ? recipe.metadata.categories 
              : (categories.length > 0 ? categories : undefined));
          const recipeAllergens = recipe.metadata?.allergens?.length > 0 
            ? recipe.metadata.allergens 
            : (allergens.length > 0 ? allergens : undefined);
          const recipeProteinSources = recipe.metadata?.proteinSources?.length > 0 
            ? recipe.metadata.proteinSources 
            : (proteinSources.length > 0 ? proteinSources : undefined);

          console.log(`💾 Saving ${recipe.title} with metadata:`, {
            mealType: recipeMealType,
            categories: recipeCategories,
            allergens: recipeAllergens,
            proteinSources: recipeProteinSources
          });

          await axios.post(`${API}/recipes/save-parsed`, {
            name: recipe.title,
            meal_type: recipeMealType,
            dietary_categories: recipeCategories,
            allergens: recipeAllergens,
            protein_sources: recipeProteinSources,
            budget_category: recipe.metadata?.budget_category || recipe.metadata?.budgetCategory,
            cooking_time: recipe.metadata?.cooking_time || recipe.metadata?.cookingTime,
            spice_level: recipe.metadata?.spice_level || recipe.metadata?.spiceLevel,
            main_protein: recipe.metadata?.main_protein || recipe.metadata?.mainProtein,
            meal_prep_style: recipe.metadata?.meal_prep_style || recipe.metadata?.mealPrepStyle || 'mix',
            image_url: recipe.image_url || null,
            instructions: recipe.instructions,
            ingredients: recipe.ingredients.map(ing => ({
              food_bank_id: ing.food_bank_id,
              grams: ing.grams,
              display_text: ing.display_text
            }))
          });
          savedCount++;
        } catch (error) {
          failedCount++;
          console.error(`Failed to save ${recipe.title}:`, error);
        }
      }

      if (savedCount > 0) {
        toast.success(`Saved ${savedCount} recipes to library${failedCount > 0 ? `, ${failedCount} failed` : ''}`);
        
        // Clear form if all saved successfully
        if (failedCount === 0) {
          setRecipeText('');
          setParsedRecipes([]);
        }
      } else {
        toast.error('Failed to save recipes');
      }

    } catch (error) {
      toast.error('Save operation failed');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const removeRecipe = (id) => {
    setParsedRecipes(parsedRecipes.filter(r => r.id !== id));
  };

  const updateRecipeTitle = (id, newTitle) => {
    setParsedRecipes(parsedRecipes.map(r => 
      r.id === id ? { ...r, title: newTitle } : r
    ));
  };

  const handleImageUpload = async (recipeId, file) => {
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    // Set uploading state
    setParsedRecipes(recipes => recipes.map(r => 
      r.id === recipeId ? { ...r, uploading_image: true } : r
    ));

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload-recipe-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update recipe with image URL
      setParsedRecipes(recipes => recipes.map(r => 
        r.id === recipeId ? { 
          ...r, 
          image_url: response.data.image_url,
          uploading_image: false 
        } : r
      ));

      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
      
      // Remove uploading state
      setParsedRecipes(recipes => recipes.map(r => 
        r.id === recipeId ? { ...r, uploading_image: false } : r
      ));
    }
  };

  const updateIngredientFoodBank = (recipeId, ingredientIndex, newFoodBankId) => {
    setParsedRecipes(parsedRecipes.map(recipe => {
      if (recipe.id !== recipeId) return recipe;
      
      const foodBankItem = foodBankItems.find(item => item.id === newFoodBankId);
      if (!foodBankItem) return recipe;

      const updatedIngredients = [...recipe.ingredients];
      updatedIngredients[ingredientIndex] = {
        ...updatedIngredients[ingredientIndex],
        food_bank_id: newFoodBankId,
        food_bank_match: foodBankItem.name,
        confidence: 1.0
      };

      return { ...recipe, ingredients: updatedIngredients };
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <ChefHat className="w-8 h-8 text-[#1a61e8]" />
            Batch Add Recipes
          </h1>
          <p className="text-gray-600 mt-2">
            Paste up to 5-10 recipes at once, separated by "---". Review and save all at once.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <Card>
            <CardHeader>
              <CardTitle>Paste Recipes</CardTitle>
              <CardDescription>
                Separate each recipe with "---" on its own line. Max 20 recipes per batch.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="mealType">Meal Type (applies to all)</Label>
                <Select value={mealType} onValueChange={setMealType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Breakfast">Breakfast</SelectItem>
                    <SelectItem value="Main Meal">Main Meal (Lunch/Dinner)</SelectItem>
                    <SelectItem value="Snack">Snack</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  💡 Main Meal recipes are used for both lunch and dinner slots
                </p>
              </div>

              <div>
                <Label>Categories (select all that apply)</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-4 border rounded-lg bg-gray-50 max-h-[300px] overflow-y-auto">
                  {availableCategories.map(cat => (
                    <label 
                      key={cat.value} 
                      className={`flex flex-col gap-1 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                        categories.includes(cat.value)
                          ? 'border-[#1a61e8] bg-[#1a61e8]/5'
                          : 'border-gray-300 hover:border-[#1a61e8]/30 bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={categories.includes(cat.value)}
                          onChange={() => toggleCategory(cat.value)}
                          className="w-4 h-4"
                        />
                        <span className="font-semibold text-sm">{cat.label}</span>
                      </div>
                      <span className="text-xs text-gray-600 ml-6">{cat.description}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Selected: {categories.length} {categories.length === 1 ? 'category' : 'categories'}
                  {categories.length > 0 && (
                    <span className="ml-2 text-[#1a61e8] font-medium">
                      ({categories.map(c => availableCategories.find(cat => cat.value === c)?.label).join(', ')})
                    </span>
                  )}
                </p>
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  Allergens (optional - auto-detected from ingredients)
                  <Badge variant="secondary" className="text-xs">🤖 Auto</Badge>
                </Label>
                <div className="grid grid-cols-3 gap-2 p-3 border rounded-lg bg-amber-50">
                  {['Fish', 'Shellfish', 'Nuts', 'Peanuts', 'Eggs', 'Dairy', 'Soy', 'Gluten'].map(allergen => (
                    <label key={allergen} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={allergens.includes(allergen)}
                        onChange={() => {
                          setAllergens(prev =>
                            prev.includes(allergen)
                              ? prev.filter(a => a !== allergen)
                              : [...prev, allergen]
                          );
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">{allergen}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-[#1552c7] mt-1 bg-[#1a61e8]/5 p-2 rounded">
                  ✨ <strong>Auto-Detection Active:</strong> Allergens are automatically detected from Food Bank ingredients when you save recipes. Manual selection is optional for backup validation.
                </p>
              </div>

              <div>
                <Label>Protein Sources (select all that apply)</Label>
                <div className="grid grid-cols-4 gap-2 p-3 border rounded-lg bg-blue-50">
                  {['Chicken', 'Beef', 'Pork', 'Fish', 'Eggs', 'Dairy', 'Plant-Based', 'Shellfish'].map(protein => (
                    <label key={protein} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={proteinSources.includes(protein)}
                        onChange={() => {
                          setProteinSources(prev =>
                            prev.includes(protein)
                              ? prev.filter(p => p !== protein)
                              : [...prev, protein]
                          );
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">{protein}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Select all protein sources in the recipe (helps ensure variety in meal plans)
                </p>
              </div>

              <div>
                <Label htmlFor="recipeText">Recipe Text</Label>
                <p className="text-sm text-blue-600 mb-2">
                  💡 <strong>NEW:</strong> Include metadata in your recipe text and it will be auto-detected! 
                  Add lines like: "Meal Type: Breakfast", "Categories: Keto, High Protein", "Allergens: Fish, Eggs"
                </p>
                <Textarea
                  id="recipeText"
                  value={recipeText}
                  onChange={(e) => setRecipeText(e.target.value)}
                  placeholder={`Meal Type: Breakfast
Categories: Keto, High Protein
Allergens: Fish, Dairy
Protein Sources: Fish, Dairy
Estimated Time: 15 mins
Recipe Name: Salmon Scramble

Ingredients:
- 100g Salmon
- 3 Egg
- 1 tbsp Butter

Instructions:
Pan-sear salmon for 3 mins. Scramble eggs in butter. Serve together.

---

Meal Type: Dinner
Categories: High Protein
Allergens: none
Protein Sources: Chicken
Estimated Time: 20 mins
Recipe Name: Grilled Chicken Bowl

Ingredients:
- 200g Chicken Breast
- 100g Broccoli

Instructions:
Grill chicken for 6-8 mins. Steam broccoli. Serve together.`}
                  className="min-h-[400px] font-mono text-sm"
                />
              </div>

              <Button 
                onClick={handleBatchParse}
                disabled={parsing || !recipeText.trim()}
                className="w-full"
                size="lg"
              >
                {parsing ? (
                  <>
                    <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                    Parsing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Parse All Recipes
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Results Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Parsed Recipes ({parsedRecipes.length})</span>
                {parsedRecipes.length > 0 && (
                  <Button
                    onClick={handleSaveAll}
                    disabled={saving || parsedRecipes.filter(r => r.status === 'success').length === 0}
                    variant="default"
                  >
                    {saving ? 'Saving...' : `Save All (${parsedRecipes.filter(r => r.status === 'success').length})`}
                  </Button>
                )}
              </CardTitle>
              <CardDescription>
                Review and edit before saving to library
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 max-h-[600px] overflow-y-auto">
              {parsedRecipes.length === 0 ? (
                <div className="text-center text-gray-500 py-12">
                  <ChefHat className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No recipes parsed yet</p>
                  <p className="text-sm">Paste recipes and click "Parse All Recipes"</p>
                </div>
              ) : (
                parsedRecipes.map((recipe, recipeIdx) => (
                  <Card key={recipe.id} className={recipe.status === 'error' ? 'border-red-300' : ''}>
                    <CardContent className="pt-4">
                      {recipe.status === 'error' ? (
                        <div className="flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" />
                          <div className="flex-1">
                            <p className="font-medium text-red-700">{recipe.title}</p>
                            <p className="text-sm text-red-600">{recipe.error}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeRecipe(recipe.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {/* Recipe Title */}
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <Input
                              value={recipe.title}
                              onChange={(e) => updateRecipeTitle(recipe.id, e.target.value)}
                              className="font-medium flex-1"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeRecipe(recipe.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>

                          {/* Image Upload */}
                          <div className="space-y-2">
                            <Label className="text-xs text-gray-700 flex items-center gap-2">
                              Recipe Image (optional)
                              {recipe.image_url && (
                                <Badge variant="secondary" className="text-xs">✓ Uploaded</Badge>
                              )}
                            </Label>
                            <div className="flex items-center gap-3">
                              {recipe.image_url && (
                                <img 
                                  src={recipe.image_url} 
                                  alt={recipe.title}
                                  className="w-24 h-24 object-cover rounded border"
                                />
                              )}
                              <Input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageUpload(recipe.id, e.target.files[0])}
                                className="flex-1"
                              />
                            </div>
                            {recipe.uploading_image && (
                              <p className="text-xs text-blue-600">Uploading image...</p>
                            )}
                          </div>

                          {/* Confidence Badge */}
                          <Badge variant={recipe.confidence > 0.7 ? 'success' : 'warning'}>
                            {(recipe.confidence * 100).toFixed(0)}% confidence
                          </Badge>

                          {/* Metadata Display */}
                          {recipe.metadata && (
                            <div className="bg-blue-50 border border-blue-200 rounded p-3 space-y-1 text-xs">
                              <p className="font-semibold text-blue-900">Auto-Detected Metadata:</p>
                              {recipe.metadata.recipeName && (
                                <p><strong>Recipe Name:</strong> {recipe.metadata.recipeName}</p>
                              )}
                              {recipe.metadata.mealType && (
                                <p><strong>Meal Type:</strong> {recipe.metadata.mealType}</p>
                              )}
                              {recipe.metadata.categories?.length > 0 && (
                                <p><strong>Categories:</strong> {recipe.metadata.categories.join(', ')}</p>
                              )}
                              {recipe.metadata.allergens?.length > 0 && (
                                <p className="flex items-center gap-2">
                                  <strong>Allergens:</strong> 
                                  <span>{recipe.metadata.allergens.join(', ')}</span>
                                  <Badge variant="secondary" className="text-xs bg-[#1a61e8]/10 text-[#1552c7]">
                                    🤖 Auto-detected
                                  </Badge>
                                </p>
                              )}
                              {recipe.metadata.allergens?.length === 0 && (
                                <p className="text-gray-500 italic">
                                  ✓ No allergens detected (safe for all)
                                </p>
                              )}
                              {recipe.metadata.proteinSources?.length > 0 && (
                                <p><strong>Protein Sources:</strong> {recipe.metadata.proteinSources.join(', ')}</p>
                              )}
                              {recipe.metadata.estimatedTime && (
                                <p><strong>Estimated Time:</strong> {recipe.metadata.estimatedTime}</p>
                              )}
                              {(recipe.metadata.budget_category || recipe.metadata.budgetCategory) && (
                                <p><strong>Budget:</strong> {recipe.metadata.budget_category || recipe.metadata.budgetCategory}</p>
                              )}
                              {(recipe.metadata.cooking_time || recipe.metadata.cookingTime) && (
                                <p><strong>Cooking Time:</strong> {recipe.metadata.cooking_time || recipe.metadata.cookingTime}</p>
                              )}
                              {(recipe.metadata.spice_level || recipe.metadata.spiceLevel) && (
                                <p><strong>Spice Level:</strong> {recipe.metadata.spice_level || recipe.metadata.spiceLevel}</p>
                              )}
                              {(recipe.metadata.main_protein || recipe.metadata.mainProtein) && (
                                <p><strong>Main Protein:</strong> {recipe.metadata.main_protein || recipe.metadata.mainProtein}</p>
                              )}
                              {(recipe.metadata.meal_prep_style || recipe.metadata.mealPrepStyle) && (
                                <p><strong>Meal Prep Style:</strong> {(recipe.metadata.meal_prep_style || recipe.metadata.mealPrepStyle || 'mix').replace(/_/g, ' ')}</p>
                              )}
                            </div>
                          )}

                          {/* Ingredients */}
                          <div className="space-y-2">
                            <Label className="text-xs text-gray-500">
                              Ingredients ({recipe.ingredients.length})
                            </Label>
                            {recipe.ingredients.map((ing, idx) => (
                              <div key={idx} className={`flex items-start gap-2 text-sm p-2 rounded ${
                                ing.confidence < 0.65 ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'
                              }`}>
                                <div className="flex-1 space-y-1">
                                  <div className="font-medium text-gray-700">
                                    {ing.display_text}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">Matched to:</span>
                                    <Select
                                      value={ing.food_bank_id || 'none'}
                                      onValueChange={(value) => updateIngredientFoodBank(recipe.id, idx, value === 'none' ? null : value)}
                                    >
                                      <SelectTrigger className="w-[250px] h-8 text-xs">
                                        <SelectValue placeholder="Select food item...">
                                          {ing.food_bank_match || 'Not matched'}
                                        </SelectValue>
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="none">
                                          <span className="text-gray-400 italic">Not matched</span>
                                        </SelectItem>
                                        {foodBankItems
                                          .sort((a, b) => a.name.localeCompare(b.name))
                                          .map(item => (
                                            <SelectItem key={item.id} value={item.id}>
                                              {item.name}
                                            </SelectItem>
                                          ))}
                                      </SelectContent>
                                    </Select>
                                    <span className="text-xs text-gray-500">{Math.round(ing.grams)}g</span>
                                  </div>
                                  {ing.confidence < 0.65 && (
                                    <div className="flex items-center gap-2 text-xs text-amber-700">
                                      <AlertCircle className="w-3 h-3" />
                                      <span>
                                        {ing.confidence < 0.5 
                                          ? 'No match found - please select manually' 
                                          : 'Low confidence match - please verify'}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
